import api from '../../../../api';
import { UserMaterial as BaseUserMaterial, Medialink } from './results';

type ActivityProgress = 'Initialized' | 'Started' | 'inProgress' | 'Submitted' | 'Completed';

type GradingProgress = 'FullyGraded' | 'Pending' | 'PendingManual' | 'Failed' | 'NotReady';

export type Session = {
  achievedAt: string;
  sortDate: string;
  duration: number;
  amountOfItems: number;
  session?: string;
  isZeroScore?: boolean;
  score: {
    maximum: number;
    achievable: number;
    achieved: number;
  };
  lti?: {
    activityProgress: ActivityProgress;
    gradingProgress: GradingProgress;
  };
  assignment?: {
    id: string;
    from: string;
    to: string;
  };
  openedBySystem: boolean;
  completedBySystem: boolean;
  status: 'not-started' | 'not-finished' | 'pending-grading' | 'busy' | 'finished';
};

type BaseResponse = {
  memberName: string;
  nodeName: string;
  total: number;
  sessions: Session[];
};

export type ExerciseParams = {
  moduleId: string;
  groupId: string;
  memberId: string;
  materialId: string;
};

export type HistoryMedialink = Medialink & {
  showDuration: boolean;
  node: undefined;
  href?: string;
};

export type MedialinkFreeExerciseResponse = BaseResponse & { medialink: HistoryMedialink };

export async function fetchMedialinkFreeExercises({
  moduleId,
  groupId,
  memberId,
  materialId,
}: ExerciseParams): Promise<MedialinkFreeExerciseResponse> {
  const { data } = await api.get<MedialinkFreeExerciseResponse>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/members/${memberId}/medialinks/${materialId}/history/exercise-type/free-exercises`,
  );

  return data;
}

export type MedialinkAssignmentResponse = BaseResponse & { medialink: HistoryMedialink };

export async function fetchMedialinkAssignments({
  moduleId,
  groupId,
  memberId,
  materialId,
}: ExerciseParams): Promise<MedialinkAssignmentResponse> {
  const { data } = await api.get<MedialinkAssignmentResponse>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/members/${memberId}/medialinks/${materialId}/history/exercise-type/assignments`,
  );

  return data;
}

export type UserMaterial = Omit<BaseUserMaterial, 'userMaxScore'> & {
  showScore: boolean;
  showDuration: boolean;
  href?: string;
};

export type UserMaterialAssignmentResponse = BaseResponse & { userMaterial: UserMaterial };

export async function fetchUserMaterialAssignments({
  groupId,
  materialId,
  memberId,
  moduleId,
}: ExerciseParams): Promise<UserMaterialAssignmentResponse> {
  const { data } = await api.get<UserMaterialAssignmentResponse>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/members/${memberId}/user-materials/${materialId}/history/exercise-type/assignments`,
  );

  return data;
}

export type UserMaterialFreeExerciseResponse = BaseResponse & { userMaterial: UserMaterial };

export async function fetchUserMaterialFreeExercises({
  moduleId,
  groupId,
  memberId,
  materialId,
}: ExerciseParams): Promise<UserMaterialFreeExerciseResponse> {
  const { data } = await api.get<UserMaterialFreeExerciseResponse>(
    `/studio/modules/${moduleId}/exercise-scores/groups/${groupId}/members/${memberId}/user-materials/${materialId}/history/exercise-type/free-exercises`,
  );

  return data;
}

export async function getSessions({
  exerciseType,
  moduleId,
  groupId,
  memberId,
  materialId,
  isAssignment,
}: {
  exerciseType: 'medialink' | 'user-material';
  moduleId: string;
  groupId: string;
  memberId: string;
  materialId: string;
  isAssignment: boolean;
}) {
  const exerciseParams: ExerciseParams = { moduleId, groupId, memberId, materialId };

  if (isAssignment && exerciseType === 'medialink') {
    const { medialink, ...rest } = await fetchMedialinkAssignments(exerciseParams);

    return { ...rest, material: medialink };
  }

  if (isAssignment && exerciseType === 'user-material') {
    const { userMaterial, ...rest } = await fetchUserMaterialAssignments(exerciseParams);

    return { ...rest, material: userMaterial };
  }

  if (exerciseType === 'medialink') {
    const { medialink, ...rest } = await fetchMedialinkFreeExercises(exerciseParams);

    return { ...rest, material: medialink };
  }

  const { userMaterial, ...rest } = await fetchUserMaterialFreeExercises(exerciseParams);

  return { ...rest, material: userMaterial };
}
